<script>
import { useConsult } from '@site/composables/useConsult'
import { packages } from '@site/data/packages'
import AppIcon from '@site/components/common/AppIcon.vue'

export default {
  name: 'StandardProductView',
  components: { AppIcon },
  data() {
    const { open } = useConsult()
    return {
      open,
      packages,
      coreFeatures: [
        { icon: 'tag', title: '高效上货', desc: '高效上货 + 条码录入' },
        { icon: 'scan', title: '智慧开单', desc: '扫码开单 / 快捷收银' },
        { icon: 'box', title: '进销存管理', desc: '进销存管理 + 库存预警' },
        { icon: 'user', title: '会员体系', desc: '会员体系与充值管理' },
        { icon: 'team', title: '员工管理', desc: '员工权限 + 多维度提成' },
        { icon: 'chart', title: '经营报表', desc: '经营数据报表' },
      ],
      industries: ['文创', '饰品', '潮玩', '谷子', '服装', '珠宝', '美妆', '手作', '杂货'],
    }
  },
}
</script>

<template>
  <div class="product-page">
    <!-- Hero -->
    <section class="product-hero">
      <div class="container product-hero__inner">
        <span class="product-hero__eyebrow">航富通 · 标准版</span>
        <h1>航富通 · 把门店生意管明白</h1>
        <p>为文创、饰品、潮玩、谷子、服装、珠宝、美妆、手作、杂货门店打造的开单收银与门店管理利器，高效开单收银，管好进销存</p>
        <div class="product-hero__cta">
          <button class="btn btn--primary" @click="open">立即咨询</button>
          <router-link to="/compare" class="btn btn--outline">对比 Nova</router-link>
        </div>
      </div>
      <div class="product-hero__shelf">
        <div class="product-hero__screen">
          <div class="product-hero__screen-title">门店经营概览</div>
          <div class="product-hero__screen-grid">
            <span>今日开单 ¥2,480</span><span>库存预警 3 件</span><span>会员 128 人</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 适用对象 -->
    <section class="section section--light">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">适用对象</span>
          <h2>这些行业门店，都用航富通</h2>
        </div>
        <div class="industry-chips reveal">
          <span v-for="i in industries" :key="i" class="chip chip--brand">{{ i }}</span>
        </div>
      </div>
    </section>

    <!-- 核心功能 -->
    <section class="section section--white">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">核心功能</span>
          <h2>一家门店要用的，都在这里</h2>
        </div>
        <div class="grid grid--3">
          <article v-for="(f, i) in coreFeatures" :key="i" class="card feature reveal">
            <div class="icon-badge"><AppIcon :name="f.icon" /></div>
            <h3>{{ f.title }}</h3>
            <p>{{ f.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- 掌上管店 + 小程序 -->
    <section class="section section--light">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">移动端</span>
          <h2>「富小宝」掌上管店 + 「小富在线」小程序</h2>
        </div>
        <div class="grid grid--2">
          <div class="card mobile reveal">
            <div class="icon-badge"><AppIcon name="mobile" /></div>
            <h3>「富小宝」掌上管店</h3>
            <ul><li>经营报表</li><li>远程管店</li><li>会员营销</li></ul>
          </div>
          <div class="card mobile reveal">
            <div class="icon-badge"><AppIcon name="monitor" /></div>
            <h3>「小富在线」小程序</h3>
            <ul><li>专属小程序</li><li>虚拟导购</li><li>24h 商城</li></ul>
          </div>
        </div>
      </div>
    </section>

    <!-- 硬件套餐 -->
    <section class="section section--white">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">硬件套餐</span>
          <h2>选择适合你门店的硬件组合</h2>
          <p>官方标配一口价，部分套餐暂未开放，欢迎咨询</p>
        </div>
        <div class="table-scroll reveal">
          <table class="pkg-table">
            <thead>
              <tr>
                <th>套餐类型</th>
                <th>官方标配</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in packages" :key="pkg.hardware">
                <td class="pkg-table__hw">{{ pkg.hardware }}</td>
                <td :class="{ 'pkg-table__na': !pkg.available }">
                  <div class="pkg-table__price">{{ pkg.standardPrice }}</div>
                  <div v-if="!pkg.available" class="pkg-table__detail">暂未开放</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- 底部 CTA -->
    <section class="product-cta">
      <div class="container">
        <h2>让门店生意更省心</h2>
        <p>立即咨询，获取适合你门店的航富通方案</p>
        <button class="btn btn--primary" @click="open">立即咨询</button>
      </div>
    </section>
  </div>
</template>


<style lang="scss" scoped>
.product-page { padding-top: 72px; }

.product-hero {
  background: $gradient-dark;
  color: $color-white;
  padding: 104px 0 0;
  text-align: center;

  &__eyebrow { color: rgba(255,255,255,0.62); font-weight: $weight-semibold; letter-spacing: 0.12em; font-size: 13px; text-transform: uppercase; }

  h1 { color: $color-white; font-size: $font-hero; font-weight: $weight-black; margin: 18px 0 22px; letter-spacing: -0.025em; }
  p { font-size: 19px; color: rgba(255,255,255,0.75); max-width: 620px; margin: 0 auto 36px; }
  &__cta { display: flex; justify-content: center; gap: 14px; margin-bottom: 44px; }

  &__shelf { display: flex; justify-content: center; }
  &__screen {
    width: 720px; max-width: 90%;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px 20px 0 0;
    padding: 28px;
    backdrop-filter: blur(12px) saturate(150%);
    border-bottom: none;

    &-title { color: rgba(255,255,255,0.85); font-weight: $weight-semibold; margin-bottom: 18px; font-size: 16px; }
    &-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; span { background: rgba(255,255,255,0.92); color: $color-text; border-radius: 12px; padding: 18px; font-weight: $weight-semibold; box-shadow: $shadow-sm; } }
  }
}

.industry-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; }

.feature { text-align: left; h3 { margin: 18px 0 6px; font-size: $font-card; font-weight: $weight-bold; } p { color: $color-text-muted; } }

.mobile {
  h3 { margin: 18px 0 14px; font-size: $font-card; font-weight: $weight-bold; }
  ul { display: flex; flex-direction: column; gap: 10px; li { color: $color-text-muted; font-size: 15px; display: flex; align-items: center; gap: 8px; } li::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: $color-secondary; } }
}

.pkg-table {
  width: 100%;
  border-collapse: collapse;
  background: $color-bg-white;
  border-radius: $radius-md;
  overflow: hidden;
  box-shadow: $shadow-sm;
  border: 1px solid rgba(29,29,31,0.05);

  th, td { padding: 18px 22px; text-align: center; border-bottom: 1px solid rgba(29,29,31,0.05); font-size: 15px; }
  th { background: $color-bg-light; color: $color-primary; font-weight: $weight-semibold; }
  &__hw { font-weight: $weight-bold; text-align: left; color: $color-text; }
  &__price { font-weight: $weight-bold; color: $color-primary; }
  &__na {
    .pkg-table__price { color: $color-text-light; font-weight: $weight-medium; }
    .pkg-table__detail { color: $color-text-light; opacity: 0.8; }
  }
  &__detail { font-size: 13px; color: $color-text-light; margin-top: 4px; }
}

.product-cta {
  background: $gradient-brand;
  color: $color-white;
  text-align: center;
  padding: $space-section 0;
  h2 { color: $color-white; font-size: $font-section; font-weight: $weight-black; margin-bottom: 14px; letter-spacing: -0.02em; }
  p { color: rgba(255,255,255,0.85); margin-bottom: 30px; }
}

@media (max-width: $bp-sm) {
  .product-hero h1 { font-size: 34px; }
  .product-hero__screen-grid { grid-template-columns: 1fr; }
  .product-hero__cta { flex-direction: column; align-items: center; }
  .product-cta h2 { font-size: 32px; }
}
</style>



// WEBPACK FOOTER //
// src/site/views/StandardProductView.vue